import HydrodigitalLogo from '@/components/General/HydrodigitalLogo';
import { InteractiveSimulationChartPlayer } from '@/components/InteractiveSimulation/InteractiveSimulationCardContent';
import InteractiveSimulationService from '@/domain/InteractiveSimulations/InteractiveSimulationService';
import IterationSimulationService from '@/domain/IterationSimulation/IterationSimulationService';
import { interactiveSimulationsEventsService } from '@/events/interactiveSimulations';
import useWebSocket from '@/hooks/useWebSocket';
import useStateMachines from '@/hooks/useStateMachines';
import { Button, Card, notification, Spin } from 'antd';
import { ComboboxLegacy as Combobox } from '@hydrodigital/design-system';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setLoaderText } from '../../redux/slices/loaderSlice';
import InteractiveSimulationMap from '../Simulations/InteractiveSimulationMap';
import InfrastructureSelector from './InfrastructureSelector';
import './index.scss';

const SimulationModeFloatingWindow = ({
	stateMachines,
	loading,
	error,
	selectedStrategies,
	onStrategyChange,
	disabled,
}) => {

	const allDisabled =
  Array.isArray(stateMachines) && stateMachines.length > 0 && stateMachines.every(sm => sm?.enable === false);
	
	if (error || stateMachines.length === 0 || allDisabled) {
		return null;
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
					<Spin size="small" />
				</div>
			);
		}

		if (error) {
			return <div style={{ color: '#ff4d4f', fontSize: '14px' }}>{error}</div>;
		}

		if (stateMachines.length === 0) {
			return <div style={{ color: '#999', fontSize: '14px' }}>No state machines found</div>;
		}

		return (
			<>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: '8px',
						paddingBottom: '12px',
						borderBottom: '1px solid #f0f0f0',
						marginBottom: '12px',
					}}
				>
					<span style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>Smart Control</span>
					<span style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>Strategy</span>
				</div>
				{stateMachines.map((sm) => (
					<div
						key={sm.id}
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							gap: '8px',
							alignItems: 'center',
							marginBottom: '12px',
						}}
					>
						<span style={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>{sm.name}</span>
						<Combobox
							options={[{ value: 'default', label: 'Default' }, ...sm.strategies]}
							size="small"
							value={selectedStrategies[sm.id]}
							onChange={(value, option) => onStrategyChange(sm.id, value, option)}
							placeholder="Select strategy"
							disabled={disabled}
						/>
					</div>
				))}
			</>
		);
	};

	return (
		<Card
			bordered
			title="Smart Controls"
			style={{
				position: 'fixed',
				top: '70px',
				right: '10px',
				left: 'auto',
				width: '500px',
				zIndex: 1001,
				boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
				borderRadius: '8px',
				backgroundColor: 'white',
			}}
			styles={{
				header: {
					borderBottom: 'none',
					paddingBottom: 0,
				},
				body: {
					padding: '1rem 1.5rem 1.5rem',
				},
			}}
		>
			{renderContent()}
		</Card>
	);
};

SimulationModeFloatingWindow.propTypes = {
	stateMachines: PropTypes.array.isRequired,
	loading: PropTypes.bool,
	error: PropTypes.string,
	selectedStrategies: PropTypes.object.isRequired,
	onStrategyChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

const sockerUrl = import.meta.env.VITE_APP_SOCKET_URL;
const env = import.meta.env.VITE_APP_ENV;

const SIMULATION_UNAVAILABLE_MESSAGE =
	'Interactive Simulation is temporarily unavailable because all simulation resources are currently in use. Please try again in a few minutes.';

const iterationSimulationService = new IterationSimulationService();
const interactiveSimulationService = new InteractiveSimulationService();

export default function InteractiveSimulationViewV2() {
	const subscriptionIS$ = interactiveSimulationsEventsService.getSubject();

	const interactiveSimulationData = JSON.parse(localStorage.getItem('interactiveSimulationData'));

	const {
		stateMachines,
		loading: loadingStateMachines,
		error: errorStateMachines,
	} = useStateMachines({ inpId: interactiveSimulationData?.inpId, onlyEnabled: true });
	const [selectedStrategies, setSelectedStrategies] = useState({});
	const selectedStrategiesRef = useRef(selectedStrategies);

	useEffect(() => {
		if (stateMachines.length > 0) {
			const defaultValues = {};
			stateMachines.forEach((sm) => {
				if (!selectedStrategies[sm.id]) {
					defaultValues[sm.id] = 'default';
				}
			});
			if (Object.keys(defaultValues).length > 0) {
				setSelectedStrategies((prev) => ({ ...prev, ...defaultValues }));
			}
		}
	}, [stateMachines]);

	useEffect(() => {
		selectedStrategiesRef.current = selectedStrategies;
	}, [selectedStrategies]);

	const handleStrategyChange = (stateMachineId, value, option) => {
		setSelectedStrategies((prev) => ({
			...prev,
			[stateMachineId]: value,
		}));
	};

	const route = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const isEdit = route.pathname.includes('interactive-simulation-edit');

	const {
		client: clientSocket,
		isConnected,
		reconnect,
		connectionFailed,
	} = useWebSocket(sockerUrl, true, env == 'prod');

	const simulationAbortedRef = useRef(false);

	const abortSimulation = useCallback(() => {
		if (simulationAbortedRef.current) return;
		simulationAbortedRef.current = true;
		setIsWaitingResponse(false);
		setPlaying(false);
		notification.error({
			message: 'Interactive Simulation unavailable',
			description: SIMULATION_UNAVAILABLE_MESSAGE,
			placement: 'topRight',
			duration: 0,
		});
		if (clientSocket) {
			clientSocket.deactivate();
		}
		navigate('/simulations');
	}, [clientSocket, navigate]);

	const { simulationId, locationId } = useParams();
	const [lastTimeReceived, setLastTimeReceived] = useState(null);

	const [iterationList, setIterationList] = useState([]);
	const [currentIteration, setCurrentIteration] = useState(null);
	const [currentIterationCompleted, setCurrentIterationCompleted] = useState(false);

	const [isRunningIteration, setIsRunningIteration] = useState(false);
	const [isWaitingResponse, setIsWaitingResponse] = useState(false);
	const [eventsToSend, setEventsToSend] = useState([]);
	const [eventsSended, setEventsSended] = useState([]);
	const currentTime = useRef(dayjs(interactiveSimulationData.startDate));
	const [playing, setPlaying] = useState(false);
	const [_, setMarkers] = useState([]);

	const playingRef = useRef(playing);
	const eventsToSendRef = useRef(eventsToSend);
	const sendEventsRef = useRef(null);

	useEffect(() => {
		playingRef.current = playing;
	}, [playing]);

	useEffect(() => {
		eventsToSendRef.current = eventsToSend;
	}, [eventsToSend]);

	const [__, setReplayData] = useState([]);

	const [infraestructures, setInfraestructures] = useState([]);
	const [selectedParameters, setSelectedParameters] = useState([]);
	const [leftPanelHeight, setLeftPanelHeight] = useState('58vh');

	const calculateLeftPanelHeight = useCallback(() => {
		const windowHeight = window.innerHeight;
		const headerHeight = 70;
		const drawerPadding = 32;
		const timelinePlayerHeight = 50;
		const chartHeight = 160;
		const drawerHeaderHeight = 50;
		const marginBottom = 30;

		let drawerContentHeight = drawerPadding + drawerHeaderHeight;

		if (selectedParameters?.length === 0) {
			drawerContentHeight += 100;
		} else {
			drawerContentHeight += timelinePlayerHeight + chartHeight + 10;
		}

		const availableHeight = windowHeight - headerHeight - drawerContentHeight - marginBottom;
		const maxHeight = Math.max(280, availableHeight);

		setLeftPanelHeight(`${maxHeight}px`);
	}, [selectedParameters]);

	const sendEvents = useCallback(() => {
		if (!clientSocket || !isConnected || !clientSocket.connected) {
			console.error('WebSocket not connected. Cannot send events.');
			if (connectionFailed) {
				abortSimulation();
				return;
			}
			reconnect();
			return;
		}

		const userId = localStorage.getItem('userId');
		const currentEventsToSend = eventsToSendRef.current;
		const currentStrategies = selectedStrategiesRef.current;

		const smartControls = stateMachines.map((sm) => ({
			smartControlId: sm.id,
			smartControlName: sm.name,
			strategy:
				currentStrategies[sm.id] || (sm.strategies.length > 0 ? sm.strategies[0].value : ''),
		}));

		const body = {
			timeEvent: dayjs(currentTime.current).format('YYYY-MM-DD HH:mm:ss'),
			status: 'RUNNING',
			events: currentEventsToSend
				? currentEventsToSend.map((ev) => {
						return {
							infrastructureId: ev.infrastructureId,
							mod_value: ev.mod_value,
						};
					})
				: [],
			smartControls,
		};

		try {
			clientSocket.publish({
				destination: `/users/${userId}/simulations/${simulationId}/iterations/${currentIteration.iterationId}/events`,
				body: JSON.stringify(body),
			});
		} catch (error) {
			console.error('Error publishing message:', error);
			abortSimulation();
			return;
		}
		if (currentEventsToSend.length > 0) {
			currentEventsToSend.forEach((ev) => {
				notification.success({
					message: `${ev.infraName} to ${ev.mod_value}`,
				});
			});
			addMarker(currentEventsToSend);
			setEventsSended((val) => [
				...val,
				{
					time: dayjs(currentTime.current),
					events: currentEventsToSend,
				},
			]);
		}
		interactiveSimulationsEventsService.setSubject({
			type: 'clearTemporalEvents',
		});
		setIsWaitingResponse(true);
	}, [
		clientSocket,
		isConnected,
		reconnect,
		simulationId,
		currentIteration,
		stateMachines,
		connectionFailed,
		abortSimulation,
	]);

	useEffect(() => {
		sendEventsRef.current = sendEvents;
	}, [sendEvents]);

	const addMarker = (events) => {
		const newMarker = {
			time: dayjs(currentTime.current),
			events,
		};
		setMarkers((prevMarkers) => {
			const updatedMarkers = [...prevMarkers, newMarker];
			updatedMarkers.sort((a, b) => a.time - b.time);
			return updatedMarkers;
		});
	};

	const handleAddIteration = async () => {
		if (!simulationId) return;
		if (iterationList.length === 0) {
			dispatch(setLoaderText('Building test environment. This can take a few minutes'));
		}
		const res = await iterationSimulationService.postIterationSimulation({
			simulationId,
		});
		dispatch(setLoaderText('Loading...'));
		if (!res) return;

		notification.success({
			message: 'Successful',
			description: 'Iteration created successfully',
		});
		const iterationId = JSON.parse(res.data.replaceAll('\\', '') || '{}').iterationId;
		if (iterationId) {
			setIterationList((prev) => [...prev, { iterationId, ended: false }]);
			setCurrentIteration({ iterationId, ended: false });
			setLastTimeReceived(null);
			setEventsSended([]);
			setEventsToSend([]);
			setReplayData([]);
			setMarkers([]);
			setCurrentIterationCompleted(false);
			setIsRunningIteration(true);
			setIsWaitingResponse(false);
			setPlaying(false);
			currentTime.current = dayjs(interactiveSimulationData.startDate);
			interactiveSimulationsEventsService.setSubject({
				type: 'resetGraph',
			});
		}
	};

	const handleEndSimulation = async () => {
		if (!simulationId) return;
		const res = await interactiveSimulationService.postFinishInteractiveSimulation(simulationId);
		if (!res) return;
		notification.info({
			message: `Simulation Completed`,
			description: 'Your simulation was completed successfully.',
			placement: 'topRight',
		});
		navigate(`/simulations`);
	};

	function cancelSimulation() {
		navigate(`/simulations`);
	}


	useEffect(() => {
		if (
			clientSocket &&
			isConnected &&
			clientSocket.connected &&
			currentIteration &&
			isRunningIteration
		) {
			const userId = localStorage.getItem('userId');
			let subscriptionActive = true;

			console.log('Subscribing to WebSocket topic for iteration:', currentIteration.iterationId);

			try {
				const subscription = clientSocket.subscribe(
					`/topic/reply/simulations/${simulationId}/iterations/${currentIteration.iterationId}/events/users/${userId}`,
					(message) => {
						if (!subscriptionActive) {
							console.warn('Message received after unsubscribe, ignoring');
							return;
						}

						const response = JSON.parse(message.body);
						console.log('WebSocket message received:', response?.length, 'items');

						if (!response || (Array.isArray(response) && response.length === 0)) {
							subscriptionActive = false;
							subscription.unsubscribe();
							abortSimulation();
							return;
						}

						setLastTimeReceived(currentTime.current);
						const dataToGraph = response.map((res) => {
							return {
								paramId: res.referenceId,
								value: res.data.value,
								last: res.data.last,
								time: res.data.time,
							};
						});
						setReplayData((rd) => {
							return [...rd, { data: dataToGraph, time: currentTime.current }];
						});
						interactiveSimulationsEventsService.setSubject({
							type: 'dataSocketRecived',
							data: dataToGraph,
						});
						if (response.length > 0 && response[0].data.last) {
							const endTime = dayjs(interactiveSimulationData.endDate);
							currentTime.current = endTime;
							setCurrentIterationCompleted(true);
							setIterationList((prev) => {
								const iterationsCopy = [...prev];
								const iterationIndex = iterationsCopy.findIndex((it) => {
									return it.iterationId == currentIteration.iterationId;
								});
								iterationsCopy[iterationIndex].ended = true;
								return iterationsCopy;
							});
							setCurrentIteration((ci) => {
								return { ...ci, ended: true };
							});
							setIsRunningIteration(false);
							subscriptionActive = false;
							subscription.unsubscribe();
							notification.success({
								message: 'Simulation completed successfully',
							});
						} else {
							const step = interactiveSimulationData.stepTime;
							const nextTime = currentTime.current.add(step * 60 * 1000, 'ms');
							currentTime.current = nextTime;
							if (playingRef.current && sendEventsRef.current) {
								sendEventsRef.current();
							}
						}
						setIsWaitingResponse(false);
					},
				);

				return () => {
					console.log('Unsubscribing from WebSocket topic');
					subscriptionActive = false;
					subscription.unsubscribe();
				};
			} catch (error) {
				console.error('Error subscribing to WebSocket:', error);
				if (connectionFailed) {
					abortSimulation();
				} else {
					reconnect();
				}
			}
		}
	}, [
		clientSocket,
		isConnected,
		currentIteration?.iterationId,
		isRunningIteration,
		simulationId,
		reconnect,
		connectionFailed,
		abortSimulation,
	]);

	useEffect(() => {
		if (playing && clientSocket && isConnected && !isWaitingResponse) {
			sendEvents();
		}
	}, [playing, clientSocket, isConnected]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (isWaitingResponse) {
				abortSimulation();
			}
		}, 60000);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [isWaitingResponse, abortSimulation]);

	useEffect(() => {
		if (connectionFailed) {
			abortSimulation();
		}
	}, [connectionFailed, abortSimulation]);

	useEffect(() => {
		handleAddIteration();
		subscriptionIS$.subscribe((event) => {
			if (event.type == 'playSimulation') {
				setPlaying(true);
			} else if (event.type == 'pauseSimulation') {
				setPlaying(false);
			} else if (event.type == 'setEventsToSend') {
				setEventsToSend(event.data);
			}
		});
	}, []);

	useEffect(() => {
		calculateLeftPanelHeight();

		const handleResize = () => {
			calculateLeftPanelHeight();
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [calculateLeftPanelHeight]);

	const handleSelectedParametersChange = (parameters) => {
		setSelectedParameters(parameters);
	};

	const handleLivePlay = () => {
		setPlaying(true);
	};

	const handleLivePause = () => {
		setPlaying(false);
	};

	return (
		<>
			<div className="header-interactiveSimulation">
				<HydrodigitalLogo onClick={() => {}} size="lg" />
				<h1></h1>
				<div className="vertical-separator" />
				{currentIteration && currentIteration.ended ? (
					<Button color="primary" variant="outlined" shape="round" onClick={handleEndSimulation}>
						Finish Simulation
					</Button>
				) : (
					<Button color="primary" variant="outlined" shape="round" onClick={cancelSimulation}>
						Cancel Simulation
					</Button>
				)}
			</div>
			<div
				style={{
					position: 'fixed',
					left: '10px',
					top: '70px',
					width: '350px',
					height: leftPanelHeight,
					maxHeight: leftPanelHeight,
					backgroundColor: 'white',
					border: '1px solid #d9d9d9',
					borderRadius: '6px',
					zIndex: 1000,
					overflow: 'hidden',
				}}
			>
				<InfrastructureSelector
					locationId={locationId}
					onSelectedParameters={handleSelectedParametersChange}
					initialSelectedParameters={selectedParameters}
				/>
			</div>
			<SimulationModeFloatingWindow
				stateMachines={stateMachines}
				loading={loadingStateMachines}
				error={errorStateMachines}
				selectedStrategies={selectedStrategies}
				onStrategyChange={handleStrategyChange}
				disabled={playing || isWaitingResponse}
			/>
			<InteractiveSimulationMap
				infrasEdited={[]}
				setInfraestructures={setInfraestructures}
				eventsSended={eventsSended}
				iterationId={currentIteration?.iterationId}
				lastTimeReceived={lastTimeReceived}
				isWaitingResponse={isWaitingResponse}
				isPaused={!playing}
			/>
			{/* <TimelineChart
				parameters={selectedParameters}
				startDate={dayjs(interactiveSimulationData.startDate)}
				endDate={dayjs(interactiveSimulationData.endDate)}
				simulationName={interactiveSimulationData.simulationName}
				currentTime={currentTime.current}
				events={eventsSended}
				iterations={iterationList}
				currentIteration={currentIteration}
				isWaitingResponse={isWaitingResponse}
				currentIterationCompleted={currentIterationCompleted}
				onCreateNewIteration={handleAddIteration}
			/> */}
			<InteractiveSimulationChartPlayer
				title={interactiveSimulationData.simulationName}
				chartStartDate={dayjs(interactiveSimulationData.startDate)}
				chartEndDate={dayjs(interactiveSimulationData.endDate)}
				currentIteration={currentIteration}
				iterations={iterationList}
				parameters={selectedParameters}
				isWaitingResponse={isWaitingResponse}
				currentIterationCompleted={currentIterationCompleted}
				currentTime={currentTime.current}
				onCreateNewIteration={handleAddIteration}
				isLivePlaying={playing}
				onLivePlay={handleLivePlay}
				onLivePause={handleLivePause}
				events={eventsSended}
			/>
		</>
	);
}
